/**
 * Framework-agnostic Supabase client factories.
 *
 * This package does NOT read environment variables — each app owns its own
 * env var reading and passes values to these factories. This keeps the
 * package compilable by plain tsc (no Vite import.meta.env, no process.env).
 *
 * Browser app usage:
 *   import { createBrowserClient } from '@pazportal/supabase'
 *   const supabase = createBrowserClient(url, anonKey)
 *
 * Server/Trigger.dev usage:
 *   import { createServerClient } from '@pazportal/supabase'
 *   const supabase = createServerClient(url, serviceRoleKey)
 */
import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database, Json } from './database.types';

export type { Database, Json };
export type TypedSupabaseClient = SupabaseClient<Database>;

/**
 * Browser-safe client — uses ANON key, respects RLS.
 * PKCE auth flow for SPA security (no implicit grant).
 */
export function createBrowserClient(
  supabaseUrl: string,
  supabaseAnonKey: string,
): TypedSupabaseClient {
  return createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      flowType: 'pkce',
      persistSession: true,
      detectSessionInUrl: true,
      autoRefreshToken: true,
    },
  });
}

/**
 * Server-side admin client — uses SERVICE_ROLE key, bypasses RLS.
 * Only for Trigger.dev jobs and Edge Function webhook receivers.
 *
 * SECURITY: Never call this from browser code. The service_role key
 * must only exist in server-side environment variables.
 */
export function createServerClient(
  supabaseUrl: string,
  supabaseServiceKey: string,
): TypedSupabaseClient {
  return createClient<Database>(supabaseUrl, supabaseServiceKey, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
    },
  });
}
